import React from "react";
import type { WizardTopBarVariant } from "./wizardTypes";

type Props = {
  variant: WizardTopBarVariant;
  activeIndex: number;
  total: number;

  // NEW: let parent decide what happens when you click a pill
  onStepClick?: (index: number) => void;

  // NEW: parent can decide which pills are clickable
  isStepClickable?: (index: number) => boolean;
};

function parseSegments(variant: WizardTopBarVariant) {
  return variant.split("->").map((s) => s.trim());
}

export default function WizardTopBar({
  variant,
  activeIndex,
  total,
  onStepClick,
  isStepClickable,
}: Props) {
  const segments = parseSegments(variant);

  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center gap-2">
        {segments.map((seg, idx) => {
          const isActive = idx === activeIndex;
          const isDone = idx < activeIndex;

          const clickable =
            typeof onStepClick === "function" &&
            (isStepClickable ? isStepClickable(idx) : isDone || isActive);

          const base =
            "rounded-full px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400";

          const style = isActive
            ? "bg-slate-900 text-white"
            : isDone
            ? "bg-slate-200 text-slate-900 hover:bg-slate-300"
            : "bg-slate-100 text-slate-600";

          if (!clickable) {
            return (
              <div key={`${seg}-${idx}`} className={`${base} ${style}`}>
                {seg}
              </div>
            );
          }

          return (
            <button
              key={`${seg}-${idx}`}
              type="button"
              className={`${base} ${style}`}
              onClick={() => onStepClick?.(idx)}
              aria-current={isActive ? "step" : undefined}
            >
              {seg}
            </button>
          );
        })}
      </div>

      <div className="mt-2 text-xs text-slate-600">
        Step {Math.min(activeIndex + 1, total)} of {total}
      </div>
    </div>
  );
}
